/**
 * An .xlsx reader for the browser, so the upload page can check a workbook before sending it.
 *
 * WHY IT EXISTS: the page's promise is "checked here first". For a CSV it kept that promise —
 * row count, recognised columns, the first rows as Trinetra will read them. For a workbook it
 * kept none of it: the file was accepted, nothing was shown, and the screen looked inert while
 * actually being ready. An operator who picked the wrong file (a task list, a rota, last year's
 * inventory) learned that only after uploading it.
 *
 * NO DEPENDENCY: an .xlsx is a ZIP of XML, and the browser can now inflate on its own —
 * `DecompressionStream('deflate-raw')` is in every current engine. Where it is missing the
 * caller falls back to uploading without a preview, which is exactly what happened before.
 *
 * It mirrors the server's reader (backend utils/csv/parseXlsx.js) — same entry lookup, same
 * shared/inline strings, same namespace tolerance — so what an operator sees here is what the
 * server will make of the same bytes. The SERVER remains the authority; this is a preview.
 */

const EOCD_SIG = 0x06054b50;
const CD_SIG = 0x02014b50;
const LOC_SIG = 0x04034b50;
const ZIP64_MARK = 0xffffffff;

/** Excel writes `<row>`; other producers write `<x:row>`. Both are valid, so both are matched. */
const NS = '(?:[A-Za-z_][\\w.-]*:)?';

export class XlsxReadError extends Error {}

interface Entry {
  name: string;
  method: number;
  compressedSize: number;
  localOffset: number;
  encrypted: boolean;
}

/** The ZIP central directory — names and offsets only, nothing inflated yet. */
function listEntries(view: DataView, bytes: Uint8Array): Map<string, Entry> {
  const floor = Math.max(0, bytes.length - (22 + 0xffff));
  let eocd = -1;
  for (let i = bytes.length - 22; i >= floor; i--) {
    if (view.getUint32(i, true) === EOCD_SIG) { eocd = i; break; }
  }
  if (eocd < 0) throw new XlsxReadError('This file is not a readable workbook — it has no ZIP directory.');

  const count = view.getUint16(eocd + 10, true);
  let offset = view.getUint32(eocd + 16, true);
  if (offset === ZIP64_MARK || count === 0xffff) {
    throw new XlsxReadError('This is a ZIP64 workbook, which cannot be previewed here.');
  }

  const decoder = new TextDecoder('utf-8');
  const entries = new Map<string, Entry>();
  for (let i = 0; i < count; i++) {
    if (offset + 46 > bytes.length || view.getUint32(offset, true) !== CD_SIG) break;
    const nameLength = view.getUint16(offset + 28, true);
    const extraLength = view.getUint16(offset + 30, true);
    const commentLength = view.getUint16(offset + 32, true);
    entries.set(decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength)), {
      name: decoder.decode(bytes.subarray(offset + 46, offset + 46 + nameLength)),
      method: view.getUint16(offset + 10, true),
      compressedSize: view.getUint32(offset + 20, true),
      localOffset: view.getUint32(offset + 42, true),
      encrypted: (view.getUint16(offset + 8, true) & 0x0001) !== 0,
    });
    offset += 46 + nameLength + extraLength + commentLength;
  }
  return entries;
}

/** One entry's bytes as text. Deflate goes through the platform's own inflater. */
async function readEntry(view: DataView, bytes: Uint8Array, entry: Entry): Promise<string> {
  if (entry.encrypted) throw new XlsxReadError('This workbook is password-protected, so it cannot be read.');

  const head = entry.localOffset;
  if (head + 30 > bytes.length || view.getUint32(head, true) !== LOC_SIG) {
    throw new XlsxReadError('This workbook is damaged — re-save it from Excel.');
  }
  // Lengths from the LOCAL header: the extra field legitimately differs from the central copy,
  // and using the wrong one lands the read a few bytes into the file data.
  const start = head + 30 + view.getUint16(head + 26, true) + view.getUint16(head + 28, true);
  const raw = bytes.subarray(start, start + entry.compressedSize);

  if (entry.method === 0) return new TextDecoder('utf-8').decode(raw);
  if (entry.method !== 8) throw new XlsxReadError('This workbook uses an unsupported compression method.');

  // `raw.slice()` rather than the subarray itself: TypeScript types a Uint8Array as generic over
  // ArrayBufferLike (it could be backed by a SharedArrayBuffer), and Blob only accepts a plain
  // ArrayBuffer view. The copy is the deflated bytes of one sheet — kilobytes.
  const deflated = new Uint8Array(raw.slice().buffer as ArrayBuffer);
  const stream = new Blob([deflated]).stream().pipeThrough(new DecompressionStream('deflate-raw'));
  return new TextDecoder('utf-8').decode(await new Response(stream).arrayBuffer());
}

/**
 * `&amp;` matters: a Dahua URL is `…?channel=1&subtype=0` and every ampersand in a sheet is
 * stored escaped. Left encoded, the preview would show a URL that is not the one imported.
 */
function decodeXml(text: string): string {
  return text
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&apos;/g, "'")
    .replace(/&#(\d+);/g, (_, d) => String.fromCodePoint(Number(d)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, h) => String.fromCodePoint(parseInt(h, 16)))
    .replace(/&amp;/g, '&'); // last, or "&amp;lt;" decodes twice
}

/** Every <t> inside a fragment, joined — a styled cell is split across runs. */
function textOf(xml: string): string {
  let out = '';
  const re = new RegExp(`<${NS}t(?:\\s[^>]*)?>([\\s\\S]*?)</${NS}t>|<${NS}t\\s*/>`, 'g');
  for (const m of xml.matchAll(re)) out += m[1] !== undefined ? decodeXml(m[1]) : '';
  return out;
}

function textInTag(xml: string, tag: string): string {
  const m = xml.match(new RegExp(`<${NS}${tag}(?:\\s[^>]*)?>([\\s\\S]*?)</${NS}${tag}>`));
  return m ? m[1] : '';
}

/** "A" → 0, "AA" → 26. Blank cells are absent from the XML, so this is what keeps columns aligned. */
function columnIndex(ref: string | undefined): number {
  const letters = String(ref || '').match(/^[A-Z]+/i);
  if (!letters) return -1;
  let n = 0;
  for (const ch of letters[0].toUpperCase()) n = n * 26 + (ch.charCodeAt(0) - 64);
  return n - 1;
}

/** Is a browser-side preview possible at all? False on engines without DecompressionStream. */
export function canPreviewXlsx(): boolean {
  return typeof DecompressionStream !== 'undefined';
}

/**
 * The workbook's first sheet, as rows of trimmed strings (header row included) — the same shape
 * `parseCsv` returns, so the preview code above does not care which format it got.
 */
export async function parseXlsx(buffer: ArrayBuffer): Promise<{ rows: string[][]; sheetName: string | null }> {
  const bytes = new Uint8Array(buffer);
  const view = new DataView(buffer);
  if (bytes.length < 4 || bytes[0] !== 0x50 || bytes[1] !== 0x4b) {
    throw new XlsxReadError('That file is not a workbook.');
  }

  const entries = listEntries(view, bytes);
  const workbook = entries.get('xl/workbook.xml');
  if (!workbook) throw new XlsxReadError('That ZIP is not an Excel workbook — it has no xl/workbook.xml.');

  // Which tab is first: workbook.xml lists them in tab order, and the relationship id maps to a
  // file. Resolved rather than assuming sheet1.xml, because a workbook whose first tab was added
  // later keeps its rows in sheet2.xml — and previewing the wrong tab is worse than none.
  const workbookXml = await readEntry(view, bytes, workbook);
  const firstSheet = workbookXml.match(new RegExp(`<${NS}sheet\\b[^>]*/?>`));
  const sheetName = firstSheet ? (firstSheet[0].match(/\sname="([^"]*)"/) || [])[1] || null : null;
  const relId = firstSheet ? (firstSheet[0].match(/r:id="([^"]+)"/) || [])[1] : null;

  let sheetPath: string | undefined;
  const rels = entries.get('xl/_rels/workbook.xml.rels');
  if (relId && rels) {
    const relsXml = await readEntry(view, bytes, rels);
    for (const rel of relsXml.matchAll(new RegExp(`<${NS}Relationship\\b[^>]*>`, 'g'))) {
      if ((rel[0].match(/Id="([^"]+)"/) || [])[1] !== relId) continue;
      const target = (rel[0].match(/Target="([^"]+)"/) || [])[1];
      if (target) sheetPath = target.startsWith('/') ? target.slice(1) : `xl/${target.replace(/^\.\//, '')}`;
      break;
    }
  }
  if (!sheetPath || !entries.has(sheetPath)) {
    sheetPath = entries.has('xl/worksheets/sheet1.xml')
      ? 'xl/worksheets/sheet1.xml'
      : [...entries.keys()].filter((n) => /^xl\/worksheets\/[^/]+\.xml$/.test(n)).sort()[0];
  }
  if (!sheetPath) throw new XlsxReadError('That workbook has no worksheets in it.');

  const shared: string[] = [];
  const sst = entries.get('xl/sharedStrings.xml');
  if (sst) {
    const sstXml = await readEntry(view, bytes, sst);
    const siRe = new RegExp(`<${NS}si(?:\\s[^>]*)?>([\\s\\S]*?)</${NS}si>`, 'g');
    for (const si of sstXml.matchAll(siRe)) shared.push(textOf(si[1]));
  }

  const sheetXml = await readEntry(view, bytes, entries.get(sheetPath)!);
  const rowRe = new RegExp(`<${NS}row\\b([^>]*)>([\\s\\S]*?)</${NS}row>`, 'g');
  const cellPattern = `<${NS}c\\b([^>]*?)(?:/>|>([\\s\\S]*?)</${NS}c>)`;
  const rows: string[][] = [];

  for (const rowMatch of sheetXml.matchAll(rowRe)) {
    const cells: string[] = [];
    for (const cellMatch of rowMatch[2].matchAll(new RegExp(cellPattern, 'g'))) {
      const attrs = cellMatch[1];
      const body = cellMatch[2] || '';
      const type = (attrs.match(/\st="([^"]+)"/) || [])[1] || 'n';
      const at = columnIndex((attrs.match(/\sr="([A-Z]+\d+)"/i) || [])[1]);

      let value = '';
      if (type === 's') {
        const idx = Number(textInTag(body, 'v'));
        value = Number.isInteger(idx) && shared[idx] !== undefined ? shared[idx] : '';
      } else if (type === 'inlineStr') {
        value = textOf(body);
      } else {
        value = decodeXml(textInTag(body, 'v'));
        if (type === 'b') value = value === '1' ? 'TRUE' : 'FALSE';
      }

      const index = at >= 0 ? at : cells.length;
      while (cells.length < index) cells.push('');
      cells[index] = value.trim();
    }
    if (cells.some((c) => c !== '')) rows.push(cells);
  }

  return { rows, sheetName };
}

export default parseXlsx;
